import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useGlobalMDAlert } from "context";
import { FP, StatusPill, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const norm = (v) =>
  String(v ?? "")
    .trim()
    .toUpperCase();

function buildSessionId() {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const rnd = Math.floor(Math.random() * 900) + 100;
  return `CT-${ymd}-${rnd}`;
}

function unitCode(chasis, tipo) {
  const c = norm(chasis);
  const suffix = c.slice(-4) || c.slice(0, 4) || "0000";
  return tipo === "Incautado" ? `RC${suffix}` : `MT${suffix}`;
}

function truncChasis(chasis) {
  const s = String(chasis ?? "");
  return s.length > 14 ? `${s.slice(0, 14)}…` : s;
}

function CompactStat({ label, value, color = FP.color.textPrimary }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "center",
        px: 1,
        py: 1.6,
        borderRadius: 2,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.45rem", md: "1.6rem" },
          color,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          color: FP.color.textSecondary,
          fontWeight: 700,
          fontSize: "0.62rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function UnitRow({ item, counted }) {
  const isRecuperada = item.tipo === "Incautado";
  const desc = [item.marca, item.modelo].filter(Boolean).join(" ");
  const subtitle = desc
    ? `${desc} · chasis ${truncChasis(item.chasis)}`
    : `chasis ${truncChasis(item.chasis)}`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: { xs: 1.75, md: 2 },
        py: 1.5,
        borderRadius: 2,
        border: `1px solid ${counted ? "#BBF7D0" : FP.color.border}`,
        bgcolor: counted ? FP.color.successSoft : FP.color.cardBg,
        boxShadow: FP.shadow.soft,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.95rem",
              color: FP.color.textPrimary,
              letterSpacing: "0.02em",
            }}
          >
            {unitCode(item.chasis, item.tipo)}
          </Typography>
          {isRecuperada ? (
            <Chip
              label="Recuperada"
              size="small"
              sx={{
                height: 20,
                bgcolor: FP.color.warningSoft,
                color: "#92400E",
                border: "1px solid #FCD34D",
                fontWeight: 700,
                fontSize: "0.62rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                "& .MuiChip-label": { px: 0.9 },
              }}
            />
          ) : null}
        </Box>
        <Typography
          sx={{
            mt: 0.35,
            color: FP.color.textSecondary,
            fontSize: "0.8rem",
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      <StatusPill
        label={counted ? "Contado" : "Pendiente"}
        variant={counted ? "success" : "neutral"}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

export default function ConteoInventarioSucursal() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const sessionId = useMemo(() => buildSessionId(), []);
  const sucursalCode = useMemo(() => String(localStorage.getItem("sucursal") ?? "").trim(), []);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [sucursalName, setSucursalName] = useState("");
  const [expected, setExpected] = useState([]);

  const [countedList, setCountedList] = useState([]);
  const [extrasList, setExtrasList] = useState([]);
  const countedSetRef = useRef(new Set());
  const extrasSetRef = useRef(new Set());

  const [scanValue, setScanValue] = useState("");
  const [manualValue, setManualValue] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scannerReady, setScannerReady] = useState(true);

  // Historial de conteos de la sucursal.
  const [historial, setHistorial] = useState([]);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialSel, setHistorialSel] = useState(null);

  const inputRef = useRef(null);
  const audioRef = useRef(null);
  // Mientras el diálogo de entrada manual está abierto, el input del escáner NO
  // debe robar el foco (si no, no se puede escribir en el campo manual).
  const manualOpenRef = useRef(false);
  // Bandera de montaje: evita que el input recupere el foco después de salir de
  // la pantalla (lo que dejaba el teclado bloqueado en toda la app).
  const mountedRef = useRef(true);
  // id del requestAnimationFrame pendiente, para poder cancelarlo al desmontar.
  const rafRef = useRef(0);
  // Se pone en true mientras hay un diálogo abierto (manual o historial) para no
  // pelear el foco con el "focus trap" del diálogo de MUI.
  const suppressFocusRef = useRef(false);

  const expectedByKey = useMemo(() => {
    const m = new Map();
    expected.forEach((p) => m.set(p.key, p));
    return m;
  }, [expected]);

  const focusInput = useCallback(() => {
    if (!mountedRef.current || manualOpenRef.current || suppressFocusRef.current) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!mountedRef.current || manualOpenRef.current || suppressFocusRef.current) return;
      // Solo enfocar si el input sigue conectado al documento.
      if (inputRef.current && document.body.contains(inputRef.current)) {
        inputRef.current.focus();
      }
    });
  }, []);

  // Limpieza al desmontar: marca la pantalla como inactiva, cancela el rAF
  // pendiente y cierra la AudioContext. Sin esto, el ciclo de re-enfoque seguía
  // vivo al salir y bloqueaba el teclado en el resto de la aplicación.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cancelAnimationFrame(rafRef.current);
      // Liberar el foco/IME ANTES de que el input se desmonte. En tablets/celulares
      // un input enfocado que se elimina del DOM deja el teclado virtual "colgado"
      // y luego no funciona en ningún campo hasta recargar la página.
      const el = inputRef.current;
      if (el && typeof el.blur === "function") {
        try { el.blur(); } catch (e) { /* ignore */ }
      }
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        try { document.activeElement.blur(); } catch (e) { /* ignore */ }
      }
      if (audioRef.current) {
        try { audioRef.current.close(); } catch (e) { /* ignore */ }
        audioRef.current = null;
      }
    };
  }, []);

  // Mientras un diálogo está abierto, no robamos el foco para no entrar en una
  // "guerra de foco" con el focus trap del diálogo (que puede dejar el teclado
  // sin responder).
  useEffect(() => {
    suppressFocusRef.current = manualOpen || historialOpen;
  }, [manualOpen, historialOpen]);

  const beep = useCallback((freq, durationMs, type = "sine") => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + durationMs / 1000);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const soundOk = useCallback(() => beep(900, 110), [beep]);
  const soundDup = useCallback(() => {
    beep(500, 80);
    setTimeout(() => beep(500, 80), 120);
  }, [beep]);
  const soundExtra = useCallback(() => beep(170, 320, "square"), [beep]);

  useEffect(() => {
    let cancelled = false;

    async function cargar() {
      setLoading(true);
      setLoadError("");
      try {
        const { ok, data } = await post("/api/mobile/conteoinventario_inicial");
        if (cancelled) return;

        if (!ok) {
          setLoadError(data?.Error || "No se pudo cargar el inventario de la sucursal.");
          setLoading(false);
          return;
        }

        const codigo = String(data?.sucursal ?? "").trim();
        const descripcion = String(data?.descripcion ?? "").trim();
        setSucursalName(descripcion ? `${codigo}-${descripcion}` : codigo || sucursalCode || "—");

        if (!codigo) {
          setExpected([]);
          setLoadError(
            "Tu usuario no tiene una sucursal asignada. Solicita al administrador que la configure."
          );
          setLoading(false);
          return;
        }

        const seen = new Set();
        const list = [];
        ((data && data.productos) || []).forEach((p) => {
          const chasis = p.chasis != null ? p.chasis : p.id;
          const key = norm(chasis);
          if (!key || seen.has(key)) return;
          seen.add(key);
          list.push({
            key,
            chasis: String(chasis),
            marca: p.marca || "",
            modelo: p.modelo || "",
            colore: p.colore || "",
            tipo: p.tipo || "",
          });
        });

        setExpected(list);
        setLoading(false);
        focusInput();
      } catch (err) {
        if (cancelled) return;
        setLoadError("Error cargando el inventario: " + (err?.message || "desconocido"));
        setLoading(false);
      }
    }

    cargar();
    return () => {
      cancelled = true;
    };
  }, [sucursalCode, post, focusInput]);

  const procesarScan = useCallback(
    (raw) => {
      const key = norm(raw);
      setScanValue("");
      setManualValue("");
      if (!key) {
        focusInput();
        return;
      }
      if (expectedByKey.has(key)) {
        if (countedSetRef.current.has(key)) {
          soundDup();
          setFeedback({ tone: "dup", msg: `Ya estaba contado: ${key}` });
        } else {
          countedSetRef.current.add(key);
          setCountedList((prev) => [...prev, key]);
          soundOk();
          const p = expectedByKey.get(key);
          setFeedback({
            tone: "ok",
            msg: `Contado: ${key}${p ? ` · ${[p.marca, p.modelo].filter(Boolean).join(" ")}` : ""}`,
          });
        }
      } else {
        soundExtra();
        if (!extrasSetRef.current.has(key)) {
          extrasSetRef.current.add(key);
          setExtrasList((prev) => [...prev, { key, chasis: String(raw).trim() }]);
        }
        setFeedback({ tone: "extra", msg: `Sobrante / no pertenece a esta sucursal: ${key}` });
        showAlert({
          message: `Chasis ${key} no pertenece al inventario esperado de esta sucursal`,
          color: "error",
          autoHideDuration: 3500,
        });
      }
      focusInput();
    },
    [expectedByKey, focusInput, soundOk, soundDup, soundExtra, showAlert]
  );

  const onScanSubmit = (e) => {
    e.preventDefault();
    procesarScan(scanValue);
  };

  const abrirManual = () => {
    manualOpenRef.current = true;
    setManualValue("");
    setManualOpen(true);
  };

  const cerrarManual = () => {
    manualOpenRef.current = false;
    setManualOpen(false);
    focusInput();
  };

  const onManualSubmit = (e) => {
    e.preventDefault();
    const val = manualValue;
    // Liberar el bloqueo ANTES de procesar, para que el escáner recupere el
    // foco al cerrar y se pueda seguir escaneando.
    manualOpenRef.current = false;
    setManualOpen(false);
    procesarScan(val);
  };

  const countedSet = useMemo(() => new Set(countedList), [countedList]);
  const pendientes = useMemo(
    () => expected.filter((p) => !countedSet.has(p.key)),
    [expected, countedSet]
  );
  const contados = useMemo(
    () => expected.filter((p) => countedSet.has(p.key)),
    [expected, countedSet]
  );

  const totalEsperado = expected.length;
  const totalContado = contados.length;
  const totalPendiente = pendientes.length;
  const totalSobrante = extrasList.length;

  const sortedUnits = useMemo(
    () => [...pendientes, ...contados],
    [pendientes, contados]
  );

  function reiniciarConteo() {
    countedSetRef.current = new Set();
    extrasSetRef.current = new Set();
    setCountedList([]);
    setExtrasList([]);
    setFeedback(null);
    setScanValue("");
    focusInput();
  }

  const cargarHistorial = useCallback(() => {
    setHistorialLoading(true);
    post("/api/mobile/conteoinventario_historial")
      .then(({ ok, data }) => setHistorial((ok && data && data.conteos) || []))
      .catch(() => setHistorial([]))
      .finally(() => setHistorialLoading(false));
  }, [post]);

  useEffect(() => {
    cargarHistorial();
  }, [cargarHistorial]);

  function abrirHistorial() {
    setHistorialSel(null);
    setHistorialOpen(true);
    cargarHistorial();
  }

  function guardarConteo() {
    if (totalEsperado === 0 && totalSobrante === 0) {
      showAlert({ message: "No hay nada que guardar", color: "warning", autoHideDuration: 3000 });
      return;
    }
    setSaving(true);
    const payload = {
      sucursal: sucursalCode,
      totalesperado: totalEsperado,
      totalcontado: totalContado,
      totalpendiente: totalPendiente,
      totalsobrante: totalSobrante,
      contados: contados.map((p) => p.chasis),
      pendientes: pendientes.map((p) => p.chasis),
      sobrantes: extrasList.map((e) => e.chasis),
    };
    post("/api/mobile/guardarconteoinventario", payload)
      .then(({ status, data }) => {
        if (status === 200) {
          setSaving(false);
          // Reiniciar el conteo para empezar uno nuevo.
          reiniciarConteo();
          cargarHistorial();
          showAlert({
            message: data?.ok || "Conteo guardado. Listo para un nuevo conteo.",
            color: "success",
            autoHideDuration: 3500,
          });
        } else if (status === 404) {
          setSaving(false);
          showAlert({
            message:
              "El endpoint de guardado aún no está disponible en el servidor (/api/mobile/guardarconteoinventario)",
            color: "error",
            autoHideDuration: 5000,
          });
        } else {
          setSaving(false);
          showAlert({
            message: data?.Error || "No se pudo guardar el conteo",
            color: "error",
            autoHideDuration: 4000,
          });
        }
      })
      .catch((err) => {
        setSaving(false);
        showAlert({
          message: "Error de red: " + err.message,
          color: "error",
          autoHideDuration: 4000,
        });
      });
  }

  const feedbackStyles = {
    ok: { bg: FP.color.successSoft, color: FP.color.successDark, icon: CheckCircleRoundedIcon },
    dup: { bg: FP.color.warningSoft, color: FP.color.warning, icon: ReportProblemRoundedIcon },
    extra: { bg: FP.color.dangerSoft, color: FP.color.danger, icon: ReportProblemRoundedIcon },
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: FP.color.surface,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          pl: { xs: 1.5, md: 3 },
          pr: { xs: 2, md: 3 },
          py: { xs: 1.75, md: 2 },
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="flex-start" gap={1.2} minWidth={0}>
            <IconButton
              size="small"
              onClick={() => {
                // Liberar el foco/IME del input del escáner ANTES de navegar para
                // que el teclado no quede bloqueado al salir.
                if (inputRef.current && typeof inputRef.current.blur === "function") {
                  try { inputRef.current.blur(); } catch (e) { /* ignore */ }
                }
                navigate("/motores/informemotores");
              }}
              sx={{
                mt: 0.2,
                color: FP.color.textSecondary,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.4,
                width: 36,
                height: 36,
                "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box minWidth={0}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.15rem", md: "1.25rem" },
                  color: FP.color.textPrimary,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                }}
              >
                Conteo semanal
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                }}
              >
                Sesión {sessionId}
                {sucursalName ? ` · ${sucursalName}` : ""}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            <IconButton
              size="small"
              onClick={abrirHistorial}
              title="Historial de conteos"
              sx={{
                color: FP.color.textSecondary,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.4,
                width: 36,
                height: 36,
                "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
              }}
            >
              <HistoryRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 0.75,
                px: 1.2,
                py: 0.6,
                borderRadius: 999,
                border: `1px solid ${scannerReady ? "#BBF7D0" : FP.color.border}`,
                bgcolor: scannerReady ? FP.color.successSoft : FP.color.surfaceAlt,
              }}
            >
              <FiberManualRecordIcon
                sx={{
                  fontSize: 10,
                  color: scannerReady ? FP.color.successDark : FP.color.textMuted,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: scannerReady ? FP.color.successDark : FP.color.textSecondary,
                  whiteSpace: "nowrap",
                }}
              >
                Escáner {scannerReady ? "listo" : "inactivo"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: 12, md: 10 },
          maxWidth: 960,
          width: "100%",
          mx: "auto",
        }}
      >
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={2}>
            <CircularProgress sx={{ color: FP.color.navy }} />
            <Typography sx={{ color: FP.color.textSecondary }}>
              Cargando inventario de la sucursal…
            </Typography>
          </Box>
        ) : loadError ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1.5}>
                <ReportProblemRoundedIcon sx={{ fontSize: 48, color: FP.color.danger }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700, textAlign: "center" }}>
                  {loadError}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Resumen */}
            <Box sx={{ display: "flex", gap: 1.2, mb: 2.5 }}>
              <CompactStat label="Esperadas" value={String(totalEsperado)} />
              <CompactStat label="Contadas" value={String(totalContado)} color={FP.color.successDark} />
              <CompactStat label="Faltan" value={String(totalPendiente)} color={FP.color.danger} />
              <CompactStat label="Extra" value={String(totalSobrante)} color={FP.color.warning} />
            </Box>

            {/* Escáner */}
            <Card
              elevation={0}
              component="form"
              onSubmit={onScanSubmit}
              onClick={focusInput}
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${FP.color.borderStrong}`,
                bgcolor: FP.color.navy,
                boxShadow: FP.shadow.navy,
                cursor: "text",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 2, md: 2.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.75,
                  "&:last-child": { pb: { xs: 2, md: 2.5 } },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.6,
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.02rem" }}>
                    Escanea una moto
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: "rgba(255,255,255,0.72)", fontSize: "0.82rem" }}>
                    Apunta el lector al código de barras de la etiqueta
                  </Typography>
                </Box>
                <TextField
                  inputRef={inputRef}
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  onFocus={() => setScannerReady(true)}
                  onBlur={isMdDown ? () => setScannerReady(false) : focusInput}
                  autoFocus
                  autoComplete="off"
                  tabIndex={0}
                  inputProps={{
                    // El lector Bluetooth (HID) escribe como teclado real; con
                    // inputMode "none" evitamos que aparezca el teclado virtual y
                    // que su IME quede colgado al salir de la pantalla.
                    inputMode: "none",
                    autoCapitalize: "characters",
                    autoCorrect: "off",
                    spellCheck: false,
                    enterKeyHint: "done",
                    "aria-label": "Escaneo de chasis",
                  }}
                  sx={{
                    position: "absolute",
                    opacity: 0,
                    width: 1,
                    height: 1,
                    pointerEvents: "none",
                    "& .MuiOutlinedInput-root": { p: 0 },
                  }}
                />
              </CardContent>
            </Card>

            {feedback ? (
              <Box
                sx={{
                  mb: 2.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 1.6,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: feedbackStyles[feedback.tone].bg,
                  color: feedbackStyles[feedback.tone].color,
                  fontWeight: 700,
                  fontSize: "0.88rem",
                }}
              >
                {React.createElement(feedbackStyles[feedback.tone].icon, { sx: { fontSize: 20 } })}
                {feedback.msg}
              </Box>
            ) : null}

            {/* Unidades esperadas */}
            <Typography
              sx={{
                mb: 1.2,
                color: FP.color.textSecondary,
                fontWeight: 700,
                fontSize: "0.68rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Unidades esperadas
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: totalSobrante > 0 ? 3 : 0 }}>
              {sortedUnits.length > 0 ? (
                sortedUnits.map((item) => (
                  <UnitRow key={item.key} item={item} counted={countedSet.has(item.key)} />
                ))
              ) : (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                    borderRadius: 2,
                    border: `1px dashed ${FP.color.border}`,
                    bgcolor: FP.color.cardBg,
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary }}>
                    No hay unidades en inventario para esta sucursal.
                  </Typography>
                </Box>
              )}
            </Box>

            {totalSobrante > 0 ? (
              <>
                <Typography
                  sx={{
                    mb: 1.2,
                    color: FP.color.danger,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  Unidades extra
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {extrasList.map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        px: { xs: 1.75, md: 2 },
                        py: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${FP.color.dangerSoft}`,
                        borderLeft: `4px solid ${FP.color.danger}`,
                        bgcolor: FP.color.cardBg,
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.95rem",
                            color: FP.color.textPrimary,
                            wordBreak: "break-all",
                          }}
                        >
                          {row.chasis}
                        </Typography>
                        <Typography sx={{ mt: 0.35, color: FP.color.danger, fontSize: "0.8rem", fontWeight: 600 }}>
                          No pertenece al inventario esperado
                        </Typography>
                      </Box>
                      <StatusPill label="Extra" variant="danger" sx={{ flexShrink: 0 }} />
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}
          </>
        )}
      </Box>

      {/* Footer fijo */}
      {!loading && !loadError ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 30,
            px: { xs: 1.5, md: 3 },
            py: 1.5,
            bgcolor: FP.color.cardBg,
            borderTop: `1px solid ${FP.color.border}`,
            boxShadow: "0 -4px 20px rgba(15,23,42,0.06)",
          }}
        >
          <Box
            sx={{
              maxWidth: 960,
              mx: "auto",
              display: "flex",
              gap: 1.2,
            }}
          >
           {/* <Button
              variant="outlined"
              startIcon={<KeyboardRoundedIcon />}
              onClick={abrirManual}
              fullWidth
              sx={{
                height: 48,
                borderColor: FP.color.borderStrong,
                color: FP.color.navy,
                bgcolor: FP.color.surfaceAlt,
                borderRadius: 1.6,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.88rem",
                "&:hover": { bgcolor: FP.color.surface, borderColor: FP.color.navyMuted },
              }}
            >
              Entrada manual
            </Button>*/}
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              onClick={guardarConteo}
              disabled={saving}
              fullWidth
              sx={{
                height: 48,
                bgcolor: FP.color.successDark,
                color: "#FFFFFF",
                borderRadius: 1.6,
                textTransform: "none",
                fontWeight: 800,
                fontSize: "0.88rem",
                boxShadow: "none",
                "&:hover": { bgcolor: "#036046", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: FP.color.successSoft, color: FP.color.textMuted },
              }}
            >
              {saving ? "Guardando…" : "Cerrar y reconciliar"}
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* Diálogo entrada manual */}
      <Dialog
        open={manualOpen}
        onClose={cerrarManual}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}` },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 1 }}>
          Entrada manual
        </DialogTitle>
        <Box component="form" onSubmit={onManualSubmit}>
          <DialogContent sx={{ pt: 0 }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.88rem", mb: 2 }}>
              Digite el chasis si no puede escanearlo con la pistola.
            </Typography>
            <TextField
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              fullWidth
              autoFocus
              label="Chasis"
              placeholder="Ej. 9C2KC1670…"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                autoCapitalize: "characters",
                autoCorrect: "off",
                spellCheck: false,
              }}
              sx={fpInputSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
            <Button
              onClick={cerrarManual}
              sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textSecondary }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: FP.color.navy,
                boxShadow: "none",
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              }}
            >
              Registrar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Diálogo historial de conteos */}
      <Dialog
        open={historialOpen}
        onClose={() => setHistorialOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMdDown}
        PaperProps={{ sx: { borderRadius: { xs: 0, md: 2.5 } } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            fontWeight: 800,
            color: FP.color.textPrimary,
          }}
        >
          <Box>
            Historial de conteos
            {sucursalName ? (
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: FP.color.textSecondary }}>
                {sucursalName}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={() => setHistorialOpen(false)} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, md: 2 } }}>
          {historialLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress size={28} sx={{ color: FP.color.navy }} />
            </Box>
          ) : historial.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={1}>
              <HistoryRoundedIcon sx={{ fontSize: 40, color: FP.color.textMuted }} />
              <Typography sx={{ color: FP.color.textSecondary }}>
                Aún no hay conteos guardados para esta sucursal.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {historial.map((h) => {
                const abierto = historialSel === h.id;
                return (
                  <Box
                    key={h.id}
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 2,
                      bgcolor: FP.color.cardBg,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setHistorialSel(abierto ? null : h.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        p: 1.5,
                        cursor: "pointer",
                        "&:hover": { bgcolor: FP.color.surfaceAlt },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.9rem" }}>
                          {h.fecha || "—"}
                        </Typography>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.76rem" }}>
                          {h.usuario ? `Por ${h.usuario}` : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Chip label={`Esp ${h.totalesperado ?? 0}`} size="small" sx={{ height: 22, fontWeight: 700, fontSize: "0.66rem", bgcolor: FP.color.surfaceAlt }} />
                        <Chip label={`Cont ${h.totalcontado ?? 0}`} size="small" sx={{ height: 22, fontWeight: 700, fontSize: "0.66rem", bgcolor: FP.color.successSoft, color: FP.color.successDark }} />
                        <Chip label={`Pend ${h.totalpendiente ?? 0}`} size="small" sx={{ height: 22, fontWeight: 700, fontSize: "0.66rem", bgcolor: FP.color.warningSoft, color: "#92400E" }} />
                        <Chip label={`Sobr ${h.totalsobrante ?? 0}`} size="small" sx={{ height: 22, fontWeight: 700, fontSize: "0.66rem", bgcolor: FP.color.dangerSoft, color: FP.color.danger }} />
                      </Box>
                    </Box>
                    {abierto ? (
                      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: `1px solid ${FP.color.border}` }}>
                        {[
                          ["Contados", h.contados, FP.color.successDark],
                          ["Pendientes", h.pendientes, "#92400E"],
                          ["Sobrantes", h.sobrantes, FP.color.danger],
                        ].map(([titulo, lista, color]) => (
                          <Box key={titulo} sx={{ mt: 1.25 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color }}>
                              {titulo} ({(lista || []).length})
                            </Typography>
                            {(lista || []).length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.6 }}>
                                {(lista || []).map((ch, i) => (
                                  <Chip
                                    key={`${titulo}-${ch}-${i}`}
                                    label={ch}
                                    size="small"
                                    sx={{ height: 22, fontSize: "0.68rem", fontFamily: "monospace", bgcolor: FP.color.surfaceAlt }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography sx={{ color: FP.color.textMuted, fontSize: "0.76rem", mt: 0.3 }}>—</Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
